export interface DeliveryRow {
  order_id: string
  customer: string
  shipping_address: string
  tote_count: number
  stop?: number
}

// ─── Page geometry (US Letter, points) ──────────────────────────────────────
const PAGE_W = 612
const PAGE_H = 792
const MARGIN = 50
const RIGHT = PAGE_W - MARGIN // 562
const BOTTOM_LIMIT = 60
const LINE_H = 13

// Column x positions
const X_STOP = MARGIN
const X_COMPANY = MARGIN + 34
const X_ADDRESS = MARGIN + 220
const X_TOTES = RIGHT - 50

// ─── helpers ─────────────────────────────────────────────────────────────────

function num(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

/** Escape a text string for a PDF literal and drop chars outside WinAnsi (>255). */
function pdfText(s: string): string {
  let out = ''
  for (const ch of s) {
    const code = ch.codePointAt(0) ?? 0
    if (code > 255) {
      out += '?'
      continue
    }
    const c = String.fromCharCode(code)
    if (c === '\\') out += '\\\\'
    else if (c === '(') out += '\\('
    else if (c === ')') out += '\\)'
    else out += c
  }
  return out
}

function addressLines(raw: string): string[] {
  return raw
    .split(/\\n|\n/)
    .map(p => p.trim())
    .filter(Boolean)
}

// ─── content-stream operator builders ────────────────────────────────────────

function text(x: number, y: number, size: number, font: 'F1' | 'F2', value: string): string {
  return `BT /${font} ${num(size)} Tf 1 0 0 1 ${num(x)} ${num(y)} Tm (${pdfText(value)}) Tj ET\n`
}

function line(x1: number, y1: number, x2: number, y2: number, width = 1, gray = 0): string {
  return `${num(width)} w ${num(gray)} ${num(gray)} ${num(gray)} RG ${num(x1)} ${num(y1)} m ${num(x2)} ${num(y2)} l S\n`
}

/** Fill a rectangle with a gray level (0 = black, 1 = white), then reset fill to black. */
function fillRectGray(x: number, y: number, w: number, h: number, gray: number): string {
  return `${num(gray)} ${num(gray)} ${num(gray)} rg ${num(x)} ${num(y)} ${num(w)} ${num(h)} re f 0 0 0 rg\n`
}

// ─── PDF assembly ────────────────────────────────────────────────────────────

function buildPdf(pageContents: string[]): ArrayBuffer {
  const pageCount = pageContents.length
  const kids: string[] = []
  for (let i = 0; i < pageCount; i++) kids.push(`${5 + 2 * i} 0 R`)

  const objects: { num: number; body: string }[] = []
  objects.push({ num: 1, body: '<< /Type /Catalog /Pages 2 0 R >>' })
  objects.push({ num: 2, body: `<< /Type /Pages /Kids [${kids.join(' ')}] /Count ${pageCount} >>` })
  objects.push({ num: 3, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>' })
  objects.push({ num: 4, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>' })

  for (let i = 0; i < pageCount; i++) {
    const pageObj = 5 + 2 * i
    const contentObj = 6 + 2 * i
    objects.push({
      num: pageObj,
      body: `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentObj} 0 R >>`,
    })
    const content = pageContents[i] ?? ''
    objects.push({
      num: contentObj,
      body: `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    })
  }

  objects.sort((a, b) => a.num - b.num)

  let pdf = '%PDF-1.4\n'
  const offsets: number[] = []
  for (const obj of objects) {
    offsets[obj.num] = pdf.length
    pdf += `${obj.num} 0 obj\n${obj.body}\nendobj\n`
  }

  const xrefOffset = pdf.length
  const total = objects.length + 1
  pdf += `xref\n0 ${total}\r\n`
  pdf += '0000000000 65535 f\r\n'
  for (let n = 1; n < total; n++) {
    pdf += `${String(offsets[n] ?? 0).padStart(10, '0')} 00000 n\r\n`
  }
  pdf += `trailer\n<< /Size ${total} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

  const buffer = new ArrayBuffer(pdf.length)
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < pdf.length; i++) bytes[i] = pdf.charCodeAt(i) & 0xff
  return buffer
}

// ─── drawing ──────────────────────────────────────────────────────────────────

/** Draws the repeating column header at `top`; returns rowTop below it. */
function drawColumnHeader(ops: string[], top: number): number {
  ops.push(line(MARGIN, top, RIGHT, top, 1.2))
  const baseline = top - 14
  ops.push(text(X_STOP, baseline, 10, 'F2', '#'))
  ops.push(text(X_COMPANY, baseline, 10, 'F2', 'Company'))
  ops.push(text(X_ADDRESS, baseline, 10, 'F2', 'Delivery Address'))
  ops.push(text(X_TOTES - 6, baseline, 10, 'F2', 'Totes'))
  const ruleY = top - 20
  ops.push(line(MARGIN, ruleY, RIGHT, ruleY, 1.2))
  return ruleY
}

function clip(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}...` : s
}

/**
 * Builds a delivery manifest PDF listing completed orders (company, delivery
 * address, tote count) and opens it in a new browser tab.
 */
export async function generateDeliveryPdf(rows: DeliveryRow[]): Promise<void> {
  const pageContents: string[] = []
  let ops: string[] = ['0 0 0 rg\n0 0 0 RG\n']

  const flushPage = () => {
    pageContents.push(ops.join(''))
    ops = ['0 0 0 rg\n0 0 0 RG\n']
  }

  const dateStr = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  // ── Header ──
  ops.push(text(MARGIN, PAGE_H - MARGIN - 6, 24, 'F2', 'Delivery Manifest'))
  ops.push(text(MARGIN, PAGE_H - MARGIN - 28, 11, 'F1', `Optimized delivery route \u2014 ${dateStr}`))

  const totalTotes = rows.reduce((s, r) => s + Number(r.tote_count ?? 0), 0)
  ops.push(text(RIGHT - 200, PAGE_H - MARGIN - 6, 11, 'F1', `${rows.length} order(s)`))
  ops.push(text(RIGHT - 200, PAGE_H - MARGIN - 22, 11, 'F1', `${totalTotes} tote(s) total`))

  let rowTop = drawColumnHeader(ops, PAGE_H - MARGIN - 50)

  if (rows.length === 0) {
    ops.push(text(MARGIN, rowTop - 24, 12, 'F1', 'No orders with totes checked out.'))
    flushPage()
  } else {
    let zebra = 0
    for (const row of rows) {
      const addr = addressLines(row.shipping_address)
      const addrCount = Math.max(addr.length, 1)
      const rowH = Math.max(addrCount * LINE_H + 8, 26)

      if (rowTop - rowH < BOTTOM_LIMIT) {
        flushPage()
        rowTop = drawColumnHeader(ops, PAGE_H - MARGIN)
        zebra = 0
      }

      if (zebra % 2 === 1) {
        ops.push(fillRectGray(MARGIN, rowTop - rowH, RIGHT - MARGIN, rowH, 0.94))
      }

      const baseline = rowTop - 16
      if (typeof row.stop === 'number') {
        ops.push(text(X_STOP, baseline, 12, 'F2', String(row.stop)))
      }
      const company = clip(row.customer || '\u2014', 28)
      ops.push(text(X_COMPANY, baseline, 11, 'F2', company))
      ops.push(text(X_COMPANY, baseline - LINE_H, 9, 'F1', row.order_id))

      if (addr.length === 0) {
        ops.push(text(X_ADDRESS, baseline, 10, 'F1', '\u2014'))
      } else {
        let ay = baseline
        for (const ln of addr) {
          ops.push(text(X_ADDRESS, ay, 10, 'F1', clip(ln, 42)))
          ay -= LINE_H
        }
      }

      ops.push(text(X_TOTES, baseline, 12, 'F2', String(row.tote_count ?? 0)))

      rowTop -= rowH
      ops.push(line(MARGIN, rowTop, RIGHT, rowTop, 0.5, 0.82))
      zebra++
    }

    // Totals row
    if (rowTop - 30 < BOTTOM_LIMIT) {
      flushPage()
      rowTop = PAGE_H - MARGIN
    }
    const ty = rowTop - 18
    ops.push(text(X_ADDRESS, ty, 12, 'F2', 'Total Totes'))
    ops.push(text(X_TOTES, ty, 14, 'F2', String(totalTotes)))
    flushPage()
  }

  const bytes = buildPdf(pageContents)
  const blob = new Blob([bytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener,noreferrer')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
